import { useState, useRef } from "react";
import { Building, MapPin, Calendar, ChevronRight, ChevronLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { motion, useInView } from "framer-motion";
import AfricaMap from "./AfricaMap";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations, t } from "@/i18n/translations";

type ProjectType = "ppp" | "water" | "finance";

interface Project {
  id: string;
  year: string;
  title: { fr: string; en: string };
  client: { fr: string; en: string };
  country: { fr: string; en: string };
  type: ProjectType;
  description: { fr: string; en: string };
  tags: { fr: string[]; en: string[] };
}

const Projects = () => {
  const [filter, setFilter] = useState<ProjectType | "all">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.05 });
  const { language } = useLanguage();
  const projectsPerPage = 5;

  const projects = (translations.projectsReal as unknown as Project[]) || [];

  const filters = [
    { id: "all", label: t(translations.projects.filters.all, language), count: projects.length },
    { id: "ppp", label: t(translations.projects.filters.ppp, language), count: projects.filter((p) => p.type === "ppp").length },
    { id: "water", label: t(translations.projects.filters.water, language), count: projects.filter((p) => p.type === "water").length },
    { id: "finance", label: t(translations.projects.filters.finance, language), count: projects.filter((p) => p.type === "finance").length },
  ];

  const filteredProjects = projects.filter((p) => filter === "all" || p.type === filter);

  const totalPages = Math.ceil(filteredProjects.length / projectsPerPage) || 1;

  // Reset to first page when filter changes
  const handleFilterChange = (id: ProjectType | "all") => {
    setFilter(id);
    setCurrentPage(1);
  };

  const paginatedProjects = filteredProjects.slice(
    (currentPage - 1) * projectsPerPage,
    currentPage * projectsPerPage
  );

  // Group paginated projects by year, preserving original order
  const grouped = paginatedProjects.reduce<Record<string, Project[]>>((acc, project) => {
    if (!acc[project.year]) acc[project.year] = [];
    acc[project.year].push(project);
    return acc;
  }, {});

  // Sort year groups descending (most recent first). Handles ranges like "2025-2026".
  const yearGroups = Object.keys(grouped).sort((a, b) => {
    const endYear = (s: string) => parseInt(s.split("-").pop() || s, 10);
    return endYear(b) - endYear(a);
  });

  const getTypeColor = (type: ProjectType) => {
    switch (type) {
      case "ppp": return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800";
      case "water": return "bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950 dark:text-cyan-300 dark:border-cyan-800";
      case "finance": return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getTypeLabel = (type: ProjectType) => {
    const labels = translations.projects.typeLabels as Record<string, { fr: string; en: string }>;
    return labels[type] ? t(labels[type], language) : type;
  };

  return (
    <section id="projects" className="py-24 bg-background" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block text-[hsl(var(--brand-red))] text-sm font-semibold tracking-widest uppercase mb-3">{t(translations.projects.badge, language)}</span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">{t(translations.projects.title, language)}</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t(translations.projects.subtitle, language)}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-10 items-start">
          <motion.div
            className="lg:col-span-2 lg:sticky lg:top-32"
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-[var(--shadow-card)]">
              <AfricaMap />
            </div>
          </motion.div>

          <div className="lg:col-span-3 space-y-8">
            <motion.div
              className="flex flex-wrap gap-2"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {filters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => handleFilterChange(f.id as ProjectType | "all")}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                    filter === f.id
                      ? 'bg-[hsl(var(--brand-navy))] text-white shadow-md'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {f.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${filter === f.id ? 'bg-white/20' : 'bg-background'}`}>
                    {f.count}
                  </span>
                </button>
              ))}
            </motion.div>

            <div className="space-y-8">
              {yearGroups.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucun projet dans cette catégorie.</p>
              ) : (
                yearGroups.map((year, groupIndex) => (
                  <div key={year} className="space-y-3">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-xs font-bold tracking-widest uppercase text-[hsl(var(--brand-red))]">
                        {year}
                      </span>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                    {grouped[year].map((project, index) => (
                      <motion.div
                        key={project.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ delay: 0.3 + groupIndex * 0.1 + index * 0.05, duration: 0.4 }}
                        className="group bg-card border border-border rounded-xl p-4 sm:p-5 hover:border-[hsl(var(--brand-red)/0.3)] hover:shadow-[var(--shadow-premium)] transition-all duration-400 cursor-pointer"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="flex items-center gap-3 flex-wrap">
                              <h3 className="text-base font-bold text-foreground group-hover:text-[hsl(var(--brand-red))] transition-colors">
                                {t(project.title, language)}
                              </h3>
                              <Badge variant="outline" className={`text-[10px] shrink-0 ${getTypeColor(project.type)}`}>
                                {getTypeLabel(project.type)}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">{t(project.description, language)}</p>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1">
                              <span className="flex items-center gap-1"><Building className="h-3 w-3" />{t(project.client, language)}</span>
                              <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{t(project.country, language)}</span>
                              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{project.year}</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5 pt-1">
                              {project.tags[language].map((tag, i) => (
                                <span key={i} className="text-[10px] px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">{tag}</span>
                              ))}
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-[hsl(var(--brand-red))] group-hover:translate-x-1 transition-all shrink-0 mt-1" />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ))
              )}
            </div>

            {filteredProjects.length > projectsPerPage && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-muted hover:bg-muted/80 text-foreground"
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t(translations.projects.prev, language)}
                </button>
                <span className="text-sm text-muted-foreground px-3">
                  {t(translations.projects.pageOf, language)} {currentPage} {t(translations.projects.on, language)} {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-muted hover:bg-muted/80 text-foreground"
                >
                  {t(translations.projects.next, language)}
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}

            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.8 }}
              className="text-center pt-4"
            >
              <p className="text-sm text-muted-foreground">
                {t(translations.projects.showing, language)} <span className="font-semibold text-foreground">{filteredProjects.length}</span> {t(translations.projects.projectWord, language)}{filteredProjects.length > 1 ? "s" : ""} {t(translations.projects.on, language)} <span className="font-semibold text-foreground">{projects.length}</span>
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
